import {
	useApiGetDataAccessRequestMetrics,
	useApiGetDataAccessRequestTimeseries,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { keepPreviousData } from '@tanstack/react-query';
import { memo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuthUser } from '../../api';
import { AnalyticsBanner } from './components/AnalyticsBanner';
import { MetricsRequestCards } from './components/cards/MetricsRequestCards';
import { RequestsOverviewChart } from './components/charts/RequestsOverviewChart';
import { MetricsOverviewTab } from './components/MetricsOverviewTab';
import { MetricsOverviewTabSkeleton } from './components/MetricsOverviewTabSkeleton';
import { requestFilterOptions } from './constants';
import { useOverviewMetricDatetime } from './hooks/useMetricOverviewDatetime';
import type { MetricCardType, RequestFilterType } from './types';

interface RequestsOverviewTabControllerProps {
	onMetricCardClick?: (metricType: MetricCardType) => void;
}

export const RequestsOverviewTabController = memo(
	({ onMetricCardClick }: RequestsOverviewTabControllerProps) => {
		const { user, workspace } = useAuthUser();
		const {
			timeRangeValue,
			setTimeRangeValue,
			dateRangeOption,
			setDateRangeOption,
			dateFilter,
			setDateFilter,
		} = useOverviewMetricDatetime({ cacheKey: 'requests' });
		const navigate = useNavigate();
		const [filterType, setFilterType] = useState<RequestFilterType>('all');

		const { data: metrics, isPending: isMetricsLoading } =
			useApiGetDataAccessRequestMetrics(
				{
					queryParams: {
						workspace_id: workspace?.id ?? '',
						filter_type: filterType,
						current_state_only: true,
						...(filterType !== 'all' && { user_id: user?.id }),
					},
				},
				{ placeholderData: keepPreviousData }
			);

		const { data: timeseries, isPending: isTimeseriesLoading } =
			useApiGetDataAccessRequestTimeseries(
				{
					queryParams: {
						workspace_id: workspace?.id ?? '',
						filter_type: filterType,
						time_range: dateFilter,
						view_option: dateRangeOption,
						...(filterType !== 'all' && { user_id: user?.id }),
					},
				},
				{ placeholderData: keepPreviousData }
			);

		const handleFilterTypesChange = useCallback(
			(filterTypes: string[]) => {
				const newFilterType = filterTypes[0] as RequestFilterType;
				setFilterType(newFilterType);

				const filterLabel = requestFilterOptions.find(
					(opt) => opt.value === newFilterType
				)?.label;
				if (!filterLabel) return;

				trackEvent(
					'requests/overview/filter_change',
					{
						filter_type: filterLabel,
						filter_value: newFilterType,
					},
					user,
					workspace!
				);
			},
			[user, workspace]
		);

		const handleNavigate = useCallback(() => {
			navigate(`/analytics`);
		}, [navigate]);

		if (isMetricsLoading || isTimeseriesLoading || !timeseries || !metrics) {
			return <MetricsOverviewTabSkeleton />;
		}

		return (
			<MetricsOverviewTab
				filterOptions={[requestFilterOptions]}
				filterTypes={[filterType]}
				setFilterTypes={handleFilterTypesChange}
			>
				<MetricsRequestCards
					requestMetrics={metrics}
					onMetricCardClick={onMetricCardClick}
				/>
				<AnalyticsBanner handleNavigate={handleNavigate} />
				<RequestsOverviewChart
					timeseries={timeseries}
					dateFilter={dateFilter || '24h'}
					setDateFilter={setDateFilter}
					setTimeRangeValue={setTimeRangeValue}
					timeRangeValue={timeRangeValue}
					dateRangeOption={dateRangeOption || 'day'}
					setDateRangeOption={setDateRangeOption}
				/>
			</MetricsOverviewTab>
		);
	}
);
